import { useContext, useEffect, useMemo, useRef } from 'react';

import { HotelOffersContext } from '../contexts/HotelOffersContext';

import type {
  HotelOffersActions,
  HotelOffersState,
} from '../types/hotel-offers';
import type { GetHotelDetailPriceResponse } from '@shared/types/frontend/ubff/hotel-detail-price';
import type { Optional } from '@shared/types/utils';
import type { TaxAndFeeInclusionPolicy } from '@skyscanner-internal/hotel-unified-bff-web-http-client/dist/skyscanner/hotelunifiedbff/common.pb';

import { ROOM_DIMENSION_FILTER_KEYS } from '@hotels-hotel-detail/utils/hotel-detail-price';
import UserContext from '@shared/utils-frontend/contexts/UserContext';
import {
  differenceDaysLength,
  parseDate,
} from '@shared/utils-universal/machine-date-utils';

export const getOffersWithDebugInfo = (
  response: GetHotelDetailPriceResponse,
) => {
  const partnerOffers = response.partnerOffers || [];
  let debugInfoOffers: Array<{ offer_id: string }> = [];
  try {
    const debugInfo = response.responseContext?.debugInfo;
    debugInfoOffers = debugInfo ? JSON.parse(debugInfo)?.offers || [] : [];
  } catch {
    // do nothing
  }
  return partnerOffers.map((partner) => {
    const offers = partner.offers || [];
    const offersWithDebugInfo = offers.map((offer) => {
      // The original JSON string from server has a key named `offer_id`.
      // For simplicity, we refer to it as it is. So we need to disable camelcase rule here.
      /* eslint-disable camelcase */
      const matchedDebugInfo = debugInfoOffers.find(
        ({ offer_id }) => offer_id === offer.id,
      );
      const debugInfoStr = matchedDebugInfo
        ? JSON.stringify((({ offer_id, ...info }) => info)(matchedDebugInfo))
        : '';
      /* eslint-enable camelcase */
      return { ...offer, debugInfo: debugInfoStr };
    });
    return { ...partner, offers: offersWithDebugInfo };
  });
};

export const useHotelOffersContext = () => {
  const context = useContext(HotelOffersContext);
  return context;
};

const useResponse = () => {
  const { state } = useHotelOffersContext();
  return state?.response;
};

export const usePartnersOffersList = () => {
  const { showDebugInfo } = useContext(UserContext);
  const response = useResponse();
  if (!response) {
    return [];
  }
  return showDebugInfo
    ? getOffersWithDebugInfo(response) || []
    : response.partnerOffers || [];
};

export const usePartnerOffer = (partnerId: string) => {
  const partnersOffers = usePartnersOffersList();
  return partnersOffers.find((partner) => partner.partner.id === partnerId);
};

export const useOffersParams = () => {
  const { state } = useHotelOffersContext();
  return state?.request;
};

export const useHotelOffersActions = () => {
  const context = useHotelOffersContext();
  return context.actions as HotelOffersActions;
};

export const useRoomFilters = () => {
  const context = useHotelOffersContext();
  const { response } = context.state as HotelOffersState;
  if (!response) {
    return [];
  }
  return response.filters || [];
};

export const useRoomTypeFilterWithPrice = (
  onlyReturnMiddleSorted?: boolean,
) => {
  const filters = useRoomFilters();

  const roomTypeGroupFilter = filters.find(
    (filter) => filter.key === 'OPTION_KEY_ROOM_TYPE_GROUP',
  );

  if (!roomTypeGroupFilter) return undefined;

  if (onlyReturnMiddleSorted) {
    return {
      ...roomTypeGroupFilter,
      options: roomTypeGroupFilter.options.filter(
        (opt) => opt.option !== 'all' && opt.option !== 'other',
      ),
    };
  }

  return roomTypeGroupFilter;
};

export const useRoomFiltersWithDimensions = () => {
  const filters = useRoomFilters();

  return filters.filter((filter) =>
    ROOM_DIMENSION_FILTER_KEYS.includes(filter.key),
  );
};

export const useStay = () => {
  const context = useHotelOffersContext();
  const { request } = context.state as HotelOffersState;
  const { travellerContext } = request;

  const { adults, checkinDate, checkoutDate, childrenAges, rooms } =
    travellerContext || {};

  return useMemo(
    () => ({
      checkIn: parseDate(checkinDate),
      checkOut: parseDate(checkoutDate),
      numberOfAdults: adults,
      numberOfRooms: rooms,
      childrenAges: childrenAges || [],
    }),
    [adults, checkinDate, checkoutDate, childrenAges, rooms],
  );
};

export const usePriceContext = () => {
  const context = useHotelOffersContext();

  const { priceContext, request, response } = context.state as HotelOffersState;

  const { filters, priceType, requestFilters } = request;
  const taxAndFeeInclusionPolicyRef =
    useRef<Optional<TaxAndFeeInclusionPolicy>>(undefined);
  if (response?.taxAndFeeInclusionPolicy) {
    taxAndFeeInclusionPolicyRef.current = response.taxAndFeeInclusionPolicy;
  }

  return {
    ...priceContext,
    searchId: response?.searchId,
    requestId: response?.requestId,
    isFiltered: !!filters?.length || !!requestFilters?.priceRangeFilter?.length,
    filters,
    priceType,
    taxAndFeeInclusionPolicy: taxAndFeeInclusionPolicyRef.current,
    stay: useStay(),
    correlationId: response?.header?.skyscannerCorrelationId,
  };
};

export const usePollingStatus = () => {
  const { state } = useHotelOffersContext();
  return (
    !!state?.error ||
    state?.response?.pollingStatus === 'SEARCH_STATUS_COMPLETED'
  );
};

export const useLowestPrice = () => {
  const partnersOffers = usePartnersOffersList();
  const flatOffers = partnersOffers.flatMap(
    (partnerOffers) => partnerOffers.offers,
  );

  const lowestOfferForHotel = flatOffers.find((offer) =>
    offer.price.tags?.find(
      (tag) => tag?.tagType === 'TAG_TYPE_CHEAPEST_FOR_HOTEL',
    ),
  );

  const lowestOfferForTraveller = flatOffers.find((offer) =>
    offer.price.tags?.find(
      (tag) => tag?.tagType === 'TAG_TYPE_CHEAPEST_FOR_TRAVELLER',
    ),
  );

  return {
    lowestPriceForHotel: lowestOfferForHotel?.price?.price,
    lowestPriceForTraveller: lowestOfferForTraveller?.price.price,
  };
};

export const useMinPrice = () => {
  const stay = useStay();
  const { checkIn, checkOut } = stay;
  const context = useHotelOffersContext();
  const { response } = context.state as HotelOffersState;
  const night = differenceDaysLength(checkOut, checkIn);

  const minPriceRef = useRef<Optional<number>>(undefined);

  if (response?.priceSummary?.minTotalPrice) {
    minPriceRef.current = response.priceSummary.minTotalPrice;
  }

  useEffect(() => {
    minPriceRef.current = undefined;
  }, [stay]);

  return minPriceRef.current ? minPriceRef.current / night : undefined;
};
